import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { Button } from '../components';
import { useConsent } from './useConsent';
import {
  setMarketingConsent,
  setConsentChoices,
  acknowledgeOptOut,
  shouldShowBanner,
  shouldShowOptOutNote,
} from './consentManager';

/**
 * The consent surface. Renders at most one of three things:
 *
 *   1. Blocking banner — consent-required regions (EU/UK/EEA/CH), first visit.
 *      The pixel stays OFF until the visitor chooses. Accept / Decline.
 *   2. Opt-out note — opt-out regions (mostly US), first visit. The pixel is
 *      already live; a brief, dismissible note explains how to opt out.
 *   3. Preferences modal — opened from the footer "Cookie preferences" link
 *      anywhere, any region, so a visitor can change or withdraw consent.
 *
 * Mounted once at the app root. It reads the consent manager (the same state
 * that governs the pixel), so accepting here immediately lets the pixel load.
 */
export function ConsentBanner() {
  const consent = useConsent();
  const [prefsOpen, setPrefsOpen] = useState(false);

  // Footer link (and reopenPreferences) broadcast this event.
  useEffect(() => {
    const open = () => setPrefsOpen(true);
    window.addEventListener('entredash:open-consent-preferences', open);
    return () => window.removeEventListener('entredash:open-consent-preferences', open);
  }, []);

  const showBanner = shouldShowBanner();
  const showNote = shouldShowOptOutNote();

  return (
    <>
      {prefsOpen && (
        <PreferencesModal
          current={consent.status}
          currentAnalytics={consent.analytics}
          onClose={() => setPrefsOpen(false)}
        />
      )}

      {!prefsOpen && showBanner && (
        <div className="consent-banner" role="dialog" aria-live="polite" aria-label="Cookie consent">
          <div className="consent-banner__inner">
            <div className="consent-banner__copy">
              <strong className="consent-banner__title">Cookie settings</strong>
              <p className="consent-banner__text">
                We use cookies to run EntreDash, improve your experience and measure our marketing.
                Your ideas are never shared.{' '}
                <Link href="/cookies" className="consent-banner__link">
                  Cookie Policy
                </Link>
              </p>
            </div>
            <div className="consent-banner__actions">
              <button type="button" className="consent-banner__custom" onClick={() => setPrefsOpen(true)}>
                Customize
              </button>
              <Button variant="secondary" compact onClick={() => setMarketingConsent(false)}>
                Reject all
              </Button>
              <Button variant="spark" compact onClick={() => setMarketingConsent(true)}>
                Accept all
              </Button>
            </div>
          </div>
        </div>
      )}

      {!prefsOpen && !showBanner && showNote && (
        <div className="consent-note" role="status">
          <span className="consent-note__text">
            We use advertising and analytics tools to measure our ads. They never
            see your idea or memo content.{' '}
            <button
              type="button"
              className="consent-note__link"
              onClick={() => setMarketingConsent(false)}
            >
              Opt out
            </button>
          </span>
          <button
            type="button"
            className="consent-note__dismiss"
            aria-label="Dismiss"
            onClick={() => acknowledgeOptOut()}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Preferences modal — reachable any time from the footer. Shows the current
 * marketing choice and lets the visitor allow or decline. Essential cookies
 * are listed as always-on and non-toggleable, since they carry no tracking.
 */
function PreferencesModal({
  current,
  currentAnalytics,
  onClose,
}: {
  current: 'granted' | 'denied' | 'unknown';
  currentAnalytics: 'granted' | 'denied' | 'unknown';
  onClose: () => void;
}) {
  // Local selection seeds from the current status ('unknown' defaults to off).
  const [marketing, setMarketing] = useState(current === 'granted');
  const [analytics, setAnalytics] = useState(currentAnalytics === 'granted');

  const save = (choice: { marketing: boolean; analytics: boolean }) => {
    setConsentChoices(choice);
    onClose();
  };

  return (
    <div className="consent-modal__scrim" onClick={onClose}>
      <div
        className="consent-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Cookie settings"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="consent-modal__head">
          <h2 className="consent-modal__title">Cookie settings</h2>
          <button type="button" className="consent-modal__x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <p className="consent-modal__intro">
          Choose which cookies we can use. Your ideas, memos and drawings are never shared,
          whatever you pick.
        </p>

        <div className="consent-row consent-row--locked">
          <div className="consent-row__text">
            <span className="consent-row__name">Essential</span>
            <span className="consent-row__desc">
              Needed for the site to work, like keeping you signed in.
            </span>
          </div>
          <span className="consent-row__always">Always on</span>
        </div>

        <label className="consent-row">
          <div className="consent-row__text">
            <span className="consent-row__name">Analytics</span>
            <span className="consent-row__desc">
              Helps us understand how our pages are used, so we can improve them.
            </span>
          </div>
          <input
            type="checkbox"
            role="switch"
            className="consent-row__toggle"
            checked={analytics}
            onChange={(e) => setAnalytics(e.target.checked)}
          />
        </label>

        <label className="consent-row">
          <div className="consent-row__text">
            <span className="consent-row__name">Marketing</span>
            <span className="consent-row__desc">
              Helps us measure our advertising.
            </span>
          </div>
          <input
            type="checkbox"
            role="switch"
            className="consent-row__toggle"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
          />
        </label>

        <div className="consent-modal__foot">
          <Link href="/cookies" className="consent-modal__policy" onClick={onClose}>
            Cookie Policy
          </Link>
          <div className="consent-modal__btns">
            <Button variant="secondary" compact onClick={() => save({ marketing, analytics })}>
              Save choices
            </Button>
            <Button variant="spark" compact onClick={() => save({ marketing: true, analytics: true })}>
              Accept all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
